import { useState } from "react";
import { ConfettiCanvas } from "@/components/ConfettiCanvas";

import { Heart, X, Gift, Loader2 } from "lucide-react";
import donImage from "@/assets/don.webp";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useCartStore } from "@/stores/cartStore";
import { storefrontApiRequest, PRODUCT_BY_HANDLE_QUERY } from "@/lib/shopify";

const DONATION_HANDLE = "don-solidaire";

const AMOUNT_OPTIONS = [
  { label: "2$", index: 0 },
  { label: "5$", index: 1 },
  { label: "10$", index: 2 },
  { label: "20$", index: 3 },
];

export const DonationModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [selectedIdx, setSelectedIdx] = useState(1); // default 5$
  const [nickname, setNickname] = useState("");
  const [loading, setLoading] = useState(false);
  const addItem = useCartStore(state => state.addItem);
  const getCheckoutUrl = useCartStore(state => state.getCheckoutUrl);

  const [showSuccess, setShowSuccess] = useState(false);
  const [hearts, setHearts] = useState<{ id: number; x: number; delay: number }[]>([]);

  const spawnHearts = () => {
    const newHearts = Array.from({ length: 12 }, (_, i) => ({
      id: Date.now() + i,
      x: Math.random() * 80 + 10,
      delay: Math.random() * 0.5,
    }));
    setHearts(newHearts);
    setTimeout(() => setHearts([]), 2500);
  };

  const handleDonate = async () => {
    setLoading(true);
    try {
      const data = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, { handle: DONATION_HANDLE });
      const product = data?.data?.productByHandle;
      if (!product) {
        toast.error("Produit de don introuvable");
        return;
      }

      const variant = product.variants.edges[selectedIdx]?.node;
      if (!variant) {
        toast.error("Variante introuvable");
        return;
      }

      await addItem({
        product: { node: product },
        variantId: variant.id,
        variantTitle: variant.title,
        price: variant.price,
        quantity: 1,
        selectedOptions: variant.selectedOptions || [],
        productType: 'donation',
      });

      // Show success animation
      setShowSuccess(true);
      spawnHearts();

      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        toast.success("Don ajouté au panier ! Finalise ta commande pour payer ❤️");
        setTimeout(() => {
          const checkoutUrl = getCheckoutUrl();
          if (checkoutUrl) {
            window.open(checkoutUrl, "_blank");
          }
        }, 300);
      }, 2200);
    } catch (err) {
      console.error(err);
      toast.error("Erreur, réessaie.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedIdx(1);
    setNickname("");
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <ConfettiCanvas active={showSuccess} />
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-foreground/60 backdrop-blur-sm animate-fade-in p-4 overflow-hidden">
      {/* Floating hearts */}
      {hearts.map((h) => (
        <span
          key={h.id}
          className="fixed text-2xl pointer-events-none z-[110]"
          style={{
            left: `${h.x}%`,
            bottom: '-20px',
            animation: `floatHeart 2s ease-out ${h.delay}s forwards`,
          }}
        >
          ❤️
        </span>
      ))}

      <style>{`
        @keyframes floatHeart {
          0% { transform: translateY(0) scale(0.5) rotate(0deg); opacity: 1; }
          50% { opacity: 1; }
          100% { transform: translateY(-100vh) scale(1.3) rotate(25deg); opacity: 0; }
        }
        @keyframes successPulse {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes checkDraw {
          0% { stroke-dashoffset: 50; }
          100% { stroke-dashoffset: 0; }
        }
      `}</style>

      <div className={`relative bg-card border-2 border-primary rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in transition-all duration-500 ${showSuccess ? 'scale-105' : ''}`}>
        {showSuccess ? (
          <div className="flex flex-col items-center justify-center py-8" style={{ animation: 'successPulse 0.6s ease-out forwards' }}>
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mb-4">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="22" stroke="hsl(var(--primary))" strokeWidth="3" fill="hsl(var(--primary) / 0.1)" />
                <path
                  d="M14 25 L21 32 L34 18"
                  stroke="hsl(var(--primary))"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                  strokeDasharray="50"
                  style={{ animation: 'checkDraw 0.5s ease-out 0.3s forwards', strokeDashoffset: 50 }}
                />
              </svg>
            </div>
            <h3 className="text-xl font-display font-bold text-foreground">Merci {nickname || ''} ! 🙏</h3>
            <p className="text-muted-foreground text-sm mt-2 text-center font-body">
              Ton don de <strong className="text-primary">{AMOUNT_OPTIONS[selectedIdx].label}</strong> est ajouté au panier
            </p>
            <p className="text-xs text-muted-foreground mt-1">Redirection vers le paiement…</p>
          </div>
        ) : (
          <>
            <button onClick={handleClose} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-5">
              <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-3">
                <img src={donImage} alt="Don solidaire" className="w-full h-full object-cover" />
              </div>
              <h3 className="text-xl font-display font-bold text-foreground">
                Faire un don solidaire 🇹🇳
              </h3>
              <p className="text-muted-foreground font-body text-sm mt-1">
                100% reversé aux familles tunisiennes. Tu reçois <strong className="text-primary">5% de réduction</strong> en remerciement !
              </p>
            </div>

            <div className="grid grid-cols-4 gap-2 mb-4">
              {AMOUNT_OPTIONS.map((opt) => (
                <button
                  key={opt.index}
                  onClick={() => setSelectedIdx(opt.index)}
                  className={`py-3 rounded-xl text-sm font-display font-bold border-2 transition-all duration-200 ${
                    selectedIdx === opt.index
                      ? "border-primary bg-primary/10 text-primary scale-105 shadow-md"
                      : "border-border text-muted-foreground hover:border-primary/40"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <input
              type="text"
              maxLength={50}
              placeholder="Ton pseudo (optionnel, affiché dans le feed)"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="w-full rounded-xl border-2 border-border bg-background px-4 py-2.5 text-sm font-body mb-4 focus:border-primary focus:outline-none transition-all duration-200"
            />

            <Button
              onClick={handleDonate}
              disabled={loading}
              className="w-full gap-2 transition-transform duration-200 active:scale-95"
              size="lg"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Heart className="w-5 h-5" />
                  Payer {AMOUNT_OPTIONS[selectedIdx].label} — Don solidaire
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center mt-3 font-body">
              💳 Paiement sécurisé via Shopify. Le don est traité comme une commande.
            </p>
          </>
        )}
      </div>
      </div>
    </>
  );
};
